import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { fetchApprovalCounts } from 'src/redux/slices/pending-counts';
import { invalidateStepCache, setStepData } from 'src/redux/slices/masterDataSlice';
import { useFormContext, Controller } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Button from '@mui/material/Button';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Iconify from 'src/components/iconify/iconify';
import axiosInstance from 'src/api/axiosInstance';
import WarningIcon from '@mui/icons-material/Warning';
import { yellow } from '@mui/material/colors'; // Import yellow color
import { useSnackbar } from 'src/components/snackbar';
import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import { encryptData } from 'src/utils/Crypto';

export default function RHFAutocomplete({
  name,
  label,
  placeholder,
  helperText,
  showPlusIcon,

  removeDefaultStyle,
  prerequisiteMasters = [],
  ...other
}) {
  const { control, setValue, watch } = useFormContext();
  const [open, setOpen] = useState(false);
  const [prerequisiteWarningOpen, setPrerequisiteWarningOpen] = useState(false);
  const [dialogTitle, setDialogTitle] = useState('');
  const [newValues, setNewValues] = useState('');
  const [selectedPrerequisite, setSelectedPrerequisite] = useState(null);
  const [prerequisiteOptions, setPrerequisiteOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const extractCleanTitle = (node) => {
    if (!node) return '';
    if (typeof node === 'string' || typeof node === 'number') {
      return String(node).replace(/\*/g, '').trim();
    }
    if (Array.isArray(node)) {
      return node.map(extractCleanTitle).filter(Boolean).join(' ').replace(/\*/g, '').trim();
    }
    if (React.isValidElement(node)) {
      return extractCleanTitle(node.props.children);
    }
    return '';
  };

  const title = extractCleanTitle(label);
  const { enqueueSnackbar } = useSnackbar();
  const token = sessionStorage.getItem('token');
  const [duplicateValues, setDuplicateValues] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [stateOptions, setStateOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);
  const [invalidChars, setInvalidChars] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState(null);
  const [selectedState, setSelectedState] = useState(null);
  const [selectedCity, setSelectedCity] = useState(null);
  const [maxLimitError, setMaxLimitError] = useState(false);
  const [repeatedInputDuplicates, setRepeatedInputDuplicates] = useState([]);
  const dispatch = useDispatch();
  // Masters that require prerequisites
  const requiresPrerequisite = [
    'Primary Skills',
    'Secondary Skills',
    'Inspection Parameter',
    'Role Name',
    'University',
  ].includes(title);

  const fetchPrerequisiteOptions = async () => {
    try {
      let apiUrl = '';

      if (title === 'Primary Skills' || title === 'Secondary Skills') {
        apiUrl = `/rest/SkillCat/getData-active`;
      } else if (title === 'Role Name') {
        apiUrl = `/rest/Role/rolelevel`;
      } else if (title === 'University') {
        // Fetch country data separately
        const countryResponse = await axiosInstance.get(`/rest/university/countries`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        // Formatting country data
        const formattedCountries = countryResponse.data.map((item) => ({
          id: item.countriesId, // Country ID
          name: item.countryName, // Full Country Name
        }));

        setCountryOptions(formattedCountries);
        return; // Exit early since we already handled this case
      } else if (title === 'Inspection Parameter') {
        // Directly set the hardcoded values
        setPrerequisiteOptions([
          { id: 1, name: 'Mandatory' },
          { id: 2, name: 'Optional' },
        ]);
        return; // Exit early since we don't need an API call
      }

      if (apiUrl) {
        const response = await axiosInstance.get(apiUrl, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const formattedData = response.data.map((item) => {
          let formattedName = '';
          switch (title) {
            case 'Primary Skills':
            case 'Secondary Skills':
              formattedName = item.skillCatNm;
              break;
            case 'Delivery Unit':
              formattedName = `${item.firstName} ${item.lastName}`;
              break;
            case 'Role Name':
              formattedName = item.roleLevel;
              break;
            default:
              formattedName = '';
          }

          return {
            id: item.id || item.roleLevelId,
            name: formattedName,
            employeeCode: `${item.employeeCode || ''}`,
          };
        });

        setPrerequisiteOptions(formattedData);
      }
    } catch (error) {
      console.error('Error fetching prerequisite options:', error);
    }
  };

  // Fetch states based on selected country
  const fetchStates = async (countryId) => {
    try {
      const response = await axiosInstance.get(`/rest/university/states?countryId=${countryId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const formattedStates = response.data.map((item) => ({
        id: item.statesId,
        name: item.stateName,
      }));

      setStateOptions(formattedStates);
    } catch (error) {
      console.error('Error fetching states:', error);
    }
  };

  // Fetch cities based on selected state
  const fetchCities = async (stateId) => {
    try {
      const response = await axiosInstance.get(`/rest/university/cities?stateId=${stateId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const formattedCities = response.data.map((item) => ({
        id: item.cityId,
        name: item.cityName,
      }));

      setCityOptions(formattedCities);
    } catch (error) {
      console.error('Error fetching cities:', error);
    }
  };

  const handleOpen = () => {
    if (requiresPrerequisite) {
      fetchPrerequisiteOptions();
      setPrerequisiteWarningOpen(true);
    } else {
      setDialogTitle(`Add New ${title}`);
      setOpen(true);
    }
  };

  // Cancel button handler — reset everything here
  const handleCancel = () => {
    setNewValues('');
    setSelectedPrerequisite(null);
    setPrerequisiteOptions([]);
    setSelectedCountry(null);
    setSelectedCity(null);
    setSelectedState(null);
    setDuplicateValues([]);
    setOpen(false);
    setPrerequisiteWarningOpen(false);
  };

  // Just closes the dialog (e.g., on outside click), no reset
  const handleClose = () => {
    setOpen(false);
    setNewValues('');
    setPrerequisiteWarningOpen(false);
    setSelectedPrerequisite(null);
    setPrerequisiteOptions([]);
    setSelectedCountry(null);
    setSelectedCity(null);
    setSelectedState(null);
    setDuplicateValues([]);
  };

  const handleSubmit = async () => {
    setLoading(true);

    if (!newValues.trim()) {
      enqueueSnackbar('Please enter at least one value.', { variant: 'warning' });
      setLoading(false);
      return;
    }

    // Split values into an array.
    const valuesArray = newValues
      .split(/[\n,]+/)
      .map((value) => value.trim())
      .filter((value) => value);

    try {
      const subscr = JSON.parse(localStorage.getItem('selectedSubscrSite'));

      const reqData = {
        title,
        newValues: valuesArray,

        ...(requiresPrerequisite
          ? {
            prerequisiteName: prerequisiteMasters.join(', '),
            prerequisiteValue: (() => {
              switch (title) {
                case 'Primary Skills':
                case 'Secondary Skills':
                  return selectedPrerequisite?.name || '';
                case 'Delivery Unit':
                  return selectedPrerequisite?.employeeCode || selectedPrerequisite?.name || '';
                case 'Role Name':
                  return selectedPrerequisite?.name || '';
                case 'Inspection Parameter':
                  return selectedPrerequisite?.name || '';
                case 'University':
                  return (
                    [selectedCountry?.name, selectedState?.name, selectedCity?.name]
                      .filter(Boolean) // Removes empty values
                      .join(' | ') || ''
                  );
                default:
                  return selectedPrerequisite?.name || '';
              }
            })(),
          }
          : {}),
      };

      // First, check for duplicates.
      const duplicateResponse = await axiosInstance.post(
        `/rest/master-approvals/check-duplicates`,
        reqData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // If no duplicates, proceed to create approval.
      if (duplicateResponse.data.message === 'No duplicates found') {
        // Submit the approval creation request.
        const response = await axiosInstance.post(
          `/rest/master-approvals/request?subscrSiteId=${subscr.subscrSiteId}`,
          reqData
        );

        if (response.status === 200) {
          const responseMsg = typeof response.data === 'string' ? response.data : '';
          const autoApproved = responseMsg.toLowerCase().includes('auto-approved');

          if (autoApproved) {
            enqueueSnackbar('Data added and approved successfully!', { variant: 'success' });
          } else {
            enqueueSnackbar(
              'Request submitted successfully! It will be visible once approved by the admin.',
              { variant: 'info' }
            );
          }

          setNewValues('');
          setDuplicateValues([]);
          handleClose();

          if (autoApproved) {
            const activeStepStr = localStorage.getItem('activeStep_new');
            if (activeStepStr !== null) {
              const step = parseInt(activeStepStr, 10);
              if (!Number.isNaN(step)) {
                // 1. Invalidate Redux in-memory cache for this step
                dispatch(invalidateStepCache({ formType: 'cand', step }));
                // 2. Refetch fresh data from backend (no @Cacheable on this endpoint — always DB-fresh)
                axiosInstance
                  .get(`/rest/candidate/fetchCandMastersByStep?step=${step}`, {
                    headers: { Authorization: `Bearer ${token}` },
                  })
                  .then((res) => {
                    // 3. Push fresh data into Redux → form's useSelector updates → dropdown re-renders
                    dispatch(setStepData({ formType: 'cand', step, data: res.data }));
                  })
                  .catch((err) => {
                    console.error('[AddOn] Step data refresh failed:', err);
                  });
              }
            }
          }
          try {
            dispatch(fetchApprovalCounts());
          } catch (err) {
            console.error('Failed to update master data count:', err);
          } }
      } else {
        enqueueSnackbar('Failed to submit request. Please try again.', { variant: 'error' });
      }
    } catch (error) {
    if (error.response) {
      const errorMessage = error.response.data?.message || '';

      if (error.response.status === 400 && errorMessage.includes('already exist')) {
        //  Extract duplicates from backend (lowercase)
        const duplicatePart = errorMessage.split(':')[1] || '';
        const dupesArray = duplicatePart
          .split(',')
          .map((v) => v.trim().toLowerCase())
          .filter(Boolean);

        const uniqueDupes = Array.from(new Set(dupesArray));

        //  Get user entered values (original case)
        const inputValues = newValues
          .split(/[\n,]+/)
          .map((v) => v.trim())
          .filter((v) => v);

        //  Map duplicates to original case
        const mappedDuplicates = inputValues.filter((val) =>
          uniqueDupes.includes(val.toLowerCase())
        );

        //  Set mapped values (correct case)
        setDuplicateValues(mappedDuplicates);
      }
    } else {
      enqueueSnackbar('Submission Failed. Try again.', {
        variant: 'error',
      });
    }
  } finally {
    setLoading(false);
  }
};

const getLabel = other.getOptionLabel || ((opt) => opt.label || '');

return (
  <>
    {showPlusIcon ? (
      <Box sx={{ display: 'flex', width: '100%' }}>
        <Box sx={{ width: '90%' }}>
          <Controller
            name={name}
            control={control}
            render={({ field, fieldState: { error } }) => (
              <Autocomplete
                {...field}
                getOptionLabel={getLabel}
                onChange={(event, newValue) => {
                  if (other.multiple) {
                    // ✅ multiple mode (keep array)
                    const filteredNewValue = Array.isArray(newValue)
                      ? newValue
                        .map((val) =>
                          typeof val === 'string'
                            ? { label: val, value: val } // 👈 generic object
                            : val
                        )
                        .filter(
                          (val) =>
                            !(other.excludeValues || []).some(
                              (excluded) =>
                                getLabel(excluded).toLowerCase() === getLabel(val).toLowerCase()
                            )
                        )
                      : [];

                    setValue(name, filteredNewValue, { shouldValidate: true });
                    field.onChange(filteredNewValue);
                  } else {
                    // ✅ single mode (keep object or null, NOT array)
                    const normalized =
                      typeof newValue === 'string'
                        ? { label: newValue, value: newValue } // 👈 normalize string
                        : newValue ?? null;

                    setValue(name, normalized, { shouldValidate: true });
                    field.onChange(normalized);
                  }
                }}
                onPaste={(event) => {
                  event.preventDefault();

                  const pastedText = event.clipboardData.getData('text');
                  if (!pastedText) return;

                  const pastedItems = pastedText
                    .split(/[\n,]+/)
                    .map((item) => item.trim())
                    .filter((item) => item.length > 0);

                  const currentValue = field.value || [];

                  const allOptions = other.options || [];

                  // Match with existing options using global getLabel
                  const matched = allOptions.filter((option) =>
                    pastedItems.some(
                      (item) => getLabel(option).toLowerCase() === item.toLowerCase()
                    )
                  );

                  // Merge current + matched + free-text
                  const merged = [...currentValue, ...matched];

                  setValue(name, merged, { shouldValidate: true });
                  field.onChange(merged);
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={label}
                    placeholder={placeholder}
                    error={!!error}
                    helperText={error ? error?.message : helperText}
                  />
                )}
                {...other}
              />
            )}
          />
        </Box>

        <Box
          sx={{
            width: 50,
            height: 40,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            border: '1px solid #e5e7e9',
            backgroundColor: 'white',
            marginLeft: 1,
            borderRadius: '8px',
            transition: 'border-color 0.2s ease-in-out',
            '&:hover': { borderColor: '#000' },
          }}
        >
          <Tooltip title={`Request to Add ${title}`} placement="top" arrow>
            <IconButton onClick={handleOpen}>
              <Iconify
                icon="mage:plus-circle"
                width="24"
                height="24"
                style={{ color: '#6666FF' }}
              />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    ) : (
      <Controller
        name={name}
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Autocomplete
            {...field}
            onChange={(event, newValue) => {
              if (other.multiple) {
                //  multiple mode (keep array)
                const filteredNewValue = Array.isArray(newValue)
                  ? newValue
                    .map((val) =>
                      typeof val === 'string'
                        ? { label: val, value: val } // 👈 generic object
                        : val
                    )
                    .filter(
                      (val) =>
                        !(other.excludeValues || []).some(
                          (excluded) =>
                            getLabel(excluded).toLowerCase() === getLabel(val).toLowerCase()
                        )
                    )
                  : [];

                setValue(name, filteredNewValue, { shouldValidate: true });
                field.onChange(filteredNewValue);
              } else {
                // single mode (keep object or null, NOT array)
                const normalized =
                  typeof newValue === 'string'
                    ? { label: newValue, value: newValue } // 👈 normalize string
                    : newValue ?? null;

                setValue(name, normalized, { shouldValidate: true });
                field.onChange(normalized);
              }
            }}
            onPaste={(event) => {
              event.preventDefault();

              const pastedText = event.clipboardData.getData('text');
              if (!pastedText) return;

              const pastedItems = pastedText
                .split(/[\n,]+/)
                .map((item) => item.trim())
                .filter((item) => item.length > 0); // ✅ remove empty values

              const currentValue = field.value || [];

              const allOptions = other.options || [];

              //  Match with existing options using global getLabel
              const primarySkills = watch('primaryskl') || [];

              const matched = allOptions.filter((option) => {
                const labl = getLabel(option).toLowerCase();

                const isPasted = pastedItems.some((item) => item.toLowerCase() === labl);

                const isInPrimary = primarySkills.some(
                  (priSkill) => getLabel(priSkill).toLowerCase() === labl
                );

                return isPasted && !isInPrimary; // Exclude if already in Primary
              });

              // ✅ Merge current + matched + free-text
              const merged = [...currentValue, ...matched];

              setValue(name, merged, { shouldValidate: true });
              field.onChange(merged);
            }}
            renderInput={(params) => (
              <TextField
                label={label}
                placeholder={placeholder}
                error={!!error}
                helperText={error ? error?.message : helperText}
                {...params}
              />
            )}
            {...other}
          />
        )}
      />
    )}

    <Dialog open={prerequisiteWarningOpen} onClose={handleClose} fullWidth maxWidth="xs">
      <DialogTitle>Select {prerequisiteMasters}</DialogTitle>
      <DialogContent>
        {!selectedPrerequisite && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              backgroundColor: yellow[100],
              color: 'black',
              padding: 2,
              borderRadius: 1,
              marginBottom: 3,
            }}
          >
            <WarningIcon sx={{ marginRight: 1, fontSize: 20, color: yellow[600] }} />
            <Typography sx={{ textAlign: 'left', fontWeight: 'bold' }}>
              Before Adding <strong>{title}</strong>, Please Select a{' '}
              <strong>{prerequisiteMasters}</strong>
            </Typography>
          </Box>
        )}

        {title === 'University' ? (
          <>
            {/* Country Autocomplete */}
            <Autocomplete
              options={countryOptions}
              getOptionLabel={(option) => option.name}
              value={selectedCountry}
              onChange={(event, newValue) => {
                setSelectedCountry(newValue);
                if (newValue) fetchStates(newValue.id); // Fetch states on country selection
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <div>
                      Select Country
                      <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  placeholder="Select Country"
                  fullWidth
                />
              )}
            />
            <br />
            {/* State Autocomplete */}
            <Autocomplete
              options={stateOptions}
              getOptionLabel={(option) => option.name}
              value={selectedState}
              onChange={(event, newValue) => {
                setSelectedState(newValue);
                if (newValue) fetchCities(newValue.id); // Fetch cities on state selection
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <div>
                      Select State
                      <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  placeholder="Select State"
                  fullWidth
                />
              )}
            />

            <br />

            {/* City Autocomplete */}
            <Autocomplete
              options={cityOptions}
              getOptionLabel={(option) => option.name}
              value={selectedCity}
              onChange={(event, newValue) => setSelectedCity(newValue)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={
                    <div>
                      Select City
                      <span style={{ color: 'red', fontWeight: 'bolder' }}> *</span>
                    </div>
                  }
                  placeholder="Select City"
                  fullWidth
                />
              )}
            />
          </>
        ) : (
          <Autocomplete
            options={prerequisiteOptions}
            getOptionLabel={(option) => option.name}
            value={selectedPrerequisite}
            onChange={(event, newValue) => setSelectedPrerequisite(newValue)}
            renderInput={(params) => <TextField {...params} placeholder="Choose one" fullWidth />}
          />
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} color="error" variant="soft">
          Cancel
        </Button>
        <Button
          onClick={() => {
            if (selectedPrerequisite || selectedCountry || selectedState || selectedCity) {
              setOpen(true);
              setPrerequisiteWarningOpen(false);
            }
          }}
          color="primary"
          disabled={
            title === 'University'
              ? !(selectedCountry && selectedState && selectedCity)
              : !selectedPrerequisite
          }
          variant="soft"
        >
          Proceed
        </Button>
      </DialogActions>
    </Dialog>
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle>Add New {title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          multiline
          rows={5}
          placeholder="Enter values separated by commas or new lines"
          value={newValues}
          onChange={(e) => {
            const inputText = e.target.value;

            //  Capture invalid characters BEFORE cleaning
            const matches = inputText.match(/[^a-zA-Z0-9\s,.+\-\n]/g);
            const uniqueInvalid = matches ? [...new Set(matches)] : [];

            //  Remove invalid characters
            const cleanedText = inputText.replace(/[^a-zA-Z0-9\s,.+\-\n]/g, '');

            //  Store invalid chars for UI message
            setInvalidChars(uniqueInvalid);

            const tempArray = cleanedText
              .split(/[\n,]+/)
              .map((value) => value.trim())
              .filter((value) => value);

            //  Max 10 validation
            if (tempArray.length > 10) {
              enqueueSnackbar('Maximum 10 Request values are allowed.', {
                variant: 'warning',
              });
              setMaxLimitError(true);
              return;
            }

            setMaxLimitError(false);
            setNewValues(cleanedText);

            //  Duplicate detection (case-insensitive)
            const seen = new Map();
            const repeatedInputValues = [];

            tempArray.forEach((val) => {
              const lower = val.toLowerCase();
              if (seen.has(lower)) {
                repeatedInputValues.push(val);
              } else {
                seen.set(lower, val);
              }
            });

            setRepeatedInputDuplicates(repeatedInputValues);
          }}
          error={maxLimitError || repeatedInputDuplicates.length > 0}
          helperText={(maxLimitError && 'Maximum 10 Request values are allowed') || ''}
        />
        {/* Invalid characters message */}
        {invalidChars.length > 0 && (
          <Box
            sx={{
              color: '#d32f2f',
              backgroundColor: '#fdecea',
              padding: '8px 12px',
              borderRadius: '6px',
              mt: 1,
              fontSize: '14px',
            }}
          >
            Invalid input. Only letters, numbers, spaces, comma , + and - are allowed
          </Box>
        )}

        {/* User entered same values like aa, AA, aA */}
        {repeatedInputDuplicates.length > 0 && (
          <div style={{ color: 'red', marginTop: '0.5rem' }}>
            Same request values are not allowed: {repeatedInputDuplicates.join(', ')}
          </div>
        )}

        {/*  Duplicate values from master list (already exist) */}
        {duplicateValues.length > 0 && (
          <Box
            sx={{
              width: '100%',
              color: 'red',
              fontWeight: 600,
              backgroundColor: '#ffe6e6',
              padding: '10px',
              borderRadius: '6px',
              mt: 1,
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            ⚠ The following values already exist in Master list : {duplicateValues.join(', ')}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="error" variant="soft">
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          color="primary"
          variant="soft"
          disabled={
            loading ||
            repeatedInputDuplicates.length > 0 ||
            maxLimitError ||
            invalidChars.length > 0
          }
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  </>
);
}

RHFAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  placeholder: PropTypes.string,
  helperText: PropTypes.string,
  showPlusIcon: PropTypes.bool,
  removeDefaultStyle: PropTypes.bool,
  prerequisiteMasters: PropTypes.arrayOf(PropTypes.string),
};

RHFAutocomplete.defaultProps = {
  placeholder: '',
  helperText: '',
  showPlusIcon: false,
  removeDefaultStyle: false,
  prerequisiteMasters: [],
};
